import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import type { UnfundedCardData } from "store/types";

interface UnfundedDeal {
  name: string;
  committed: number;
  called: number;
  unfunded: number;
  pctCalled: number;
  postfix: string;
}

interface Props {
  data?: UnfundedCardData[];
  loading?: boolean;
}

function formatWithPostfix(value: number, postfix: string): string {
  if (Math.abs(value) >= 1_000_000) return `${postfix}${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${postfix}${(value / 1_000).toFixed(0)}K`;
  return `${postfix}${value.toFixed(0)}`;
}

export const UnfundedCommitmentsCard: React.FC<Props> = ({ data, loading }) => {
  const deals: UnfundedDeal[] = (data ?? [])
    .map((d) => ({
      name: d.name,
      committed: d.committed,
      called: d.called,
      unfunded: d.committed - d.called,
      pctCalled: d.committed > 0 ? d.called / d.committed : 0,
      postfix: d.postfix
    }))
    .filter((d) => d.unfunded > 0)
    .sort((a, b) => b.unfunded - a.unfunded);

  const totalUnfunded = deals.reduce((sum, d) => sum + d.unfunded, 0);
  const totalCommitted = deals.reduce((sum, d) => sum + d.committed, 0);
  const totalPctCalled = totalCommitted > 0 ? (totalCommitted - totalUnfunded) / totalCommitted : 0;
  const defaultPostfix = deals[0]?.postfix ?? "$";

  if (loading || !data) {
    return (
      <Box sx={{ p: "16px 8px 16px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#6B7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          mb: 0.5
        }}
      >
        Unfunded Commitments
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#071f35", mb: 1.5 }}>
        {formatWithPostfix(totalUnfunded, defaultPostfix)}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {deals.map((deal) => (
          <Box key={deal.name}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "4px" }}>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#243B53",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "60%"
                }}
              >
                {deal.name}
              </Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#496682" }}>
                {formatWithPostfix(deal.unfunded, deal.postfix)}
              </Typography>
            </Box>
            <Box sx={{ position: "relative", height: 6, bgcolor: "#E5E7EB", borderRadius: 3, overflow: "hidden" }}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: "100%",
                  width: `${(deal.pctCalled * 100).toFixed(1)}%`,
                  bgcolor: "#0F5D5E",
                  borderRadius: 3,
                  transition: "width 0.3s ease"
                }}
              />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: "2px" }}>
              <Typography sx={{ fontSize: 9, color: "#9CA3AF" }}>
                {(deal.pctCalled * 100).toFixed(0)}% called
              </Typography>
              <Typography sx={{ fontSize: 9, color: "#9CA3AF" }}>
                of {formatWithPostfix(deal.committed, deal.postfix)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #E5E7EB" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "4px" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#071f35" }}>Total</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#071f35" }}>
            {formatWithPostfix(totalUnfunded, defaultPostfix)}
          </Typography>
        </Box>
        <Box sx={{ position: "relative", height: 6, bgcolor: "#E5E7EB", borderRadius: 3, overflow: "hidden" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${(totalPctCalled * 100).toFixed(1)}%`,
              bgcolor: "#c09560",
              borderRadius: 3,
              transition: "width 0.3s ease"
            }}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: "2px" }}>
          <Typography sx={{ fontSize: 9, color: "#9CA3AF" }}>{(totalPctCalled * 100).toFixed(0)}% called</Typography>
          <Typography sx={{ fontSize: 9, color: "#9CA3AF" }}>
            of {formatWithPostfix(totalCommitted, defaultPostfix)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
